/**
 * TaskDescriptionAndAttachments - Descrição rica + anexos para tarefas
 * 
 * Suporta dois modos:
 * - Modo edição (taskId definido): upload direto via hook useTaskAttachments
 * - Modo criação (taskId undefined): staged files em memória, expostos via onPendingFilesChange
 * 
 * Reutiliza componentes compartilhados (AttachmentThumbnail, AttachmentUploadArea)
 * e utilitários centralizados (file-utils) para evitar duplicidade.
 * 
 * Versão: 1.2.0
 * RELEASE: 2026-04-06 v1.1.0 - Refatoração para usar componentes compartilhados
 * RELEASE: 2026-04-06 v1.2.0 - Suporte a staged files para criação atômica
 */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { ImageLightbox, MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve o tipo de mídia do preview interno (imagem/vídeo/documento)
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { Paperclip, Loader2, X } from 'lucide-react';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { useTaskAttachments, TaskAttachment, validateFile } from '@/hooks/useTaskAttachments';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { getFileIcon, getFileColor, getMimeMediaType } from '@/lib/file-utils';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Props do componente
interface TaskDescriptionAndAttachmentsProps {
  /** ID da tarefa (null/undefined em modo criação) */
  taskId?: string;
  /** Conteúdo HTML da descrição */
  descriptionHtml: string;
  /** Callback ao alterar conteúdo da descrição */
  onDescriptionChange: (html: string) => void;
  /** Callback para expor arquivos pendentes ao componente pai (modo criação) */
  onPendingFilesChange?: (files: File[]) => void;
  /** Modo somente leitura */
  readOnly?: boolean;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Miniatura de arquivo pendente (staged, ainda não enviado ao servidor).
 * Exibe preview local via URL.createObjectURL para imagens ou ícone para outros tipos.
 */
function PendingFileThumbnail({
  file,
  onRemove,
}: {
  file: File;
  onRemove: () => void;
}) {
  // Determina tipo do arquivo para ícone/preview
  const isImage = file.type.startsWith('image/');
  const fileType = isImage ? 'image'
    : file.type.startsWith('video/') ? 'video'
    : file.type.startsWith('audio/') ? 'audio'
    : file.type.includes('pdf') ? 'pdf'
    : /\.(zip|rar|7z|tar|gz)$/i.test(file.name) ? 'archive'
    : 'document';
  const FileIcon = getFileIcon(fileType);
  const colorClasses = getFileColor(fileType);

  // Gera URL local para preview de imagens
  const previewUrl = useMemo(() => {
    if (isImage) return URL.createObjectURL(file);
    return null;
  }, [file, isImage]);

  // Limpa objectURL ao desmontar
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="group relative w-16 h-16 rounded-lg border bg-background overflow-hidden flex items-center justify-center">
      {/* Preview de imagem ou ícone */}
      {isImage && previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className={cn('p-2 rounded', colorClasses)}>
          <FileIcon className="h-6 w-6" />
        </div>
      )}

      {/* Botão remover no hover */}
      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="p-1 rounded bg-destructive/80 hover:bg-destructive transition-colors"
          title="Remover"
        >
          <X className="h-3 w-3 text-white" />
        </button>
      </div>

      {/* Tooltip com nome do arquivo */}
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
        <div className="bg-popover text-popover-foreground text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap max-w-[120px] truncate">
          {file.name}
        </div>
      </div>
    </div>
  );
}

/**
 * Componente principal: Descrição rica + Anexos para tarefas
 * Usa componentes compartilhados para miniaturas e área de upload
 */
export function TaskDescriptionAndAttachments({
  taskId,
  descriptionHtml,
  onDescriptionChange,
  onPendingFilesChange,
  readOnly = false,
  className,
}: TaskDescriptionAndAttachmentsProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMediaItems, setLightboxMediaItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estado de arquivos pendentes (modo criação — staged files)
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  // Hook de anexos (só funciona quando taskId existe - modo edição)
  const {
    attachments,
    isLoading: isLoadingAttachments,
    uploadFile,
    deleteAttachment,
    isUploading,
  } = useTaskAttachments(taskId);

  // Modo criação = sem taskId
  const isCreateMode = !taskId;

  // Contagem total de anexos (existentes + pendentes)
  const existingCount = attachments?.length || 0;
  const totalCount = existingCount + pendingFiles.length;

  // Notifica o pai sobre mudanças nos arquivos pendentes
  useEffect(() => {
    onPendingFilesChange?.(pendingFiles);
  }, [pendingFiles, onPendingFilesChange]);

  // Seleção de arquivos — comportamento diferente por modo
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (isCreateMode) {
      // Modo criação: acumula em pendingFiles (staged)
      const validFiles: File[] = [];
      Array.from(files).forEach(file => {
        const validation = validateFile(file);
        if (!validation.valid) {
          toast.error(`${file.name}: ${validation.error}`);
        } else {
          validFiles.push(file);
        }
      });
      if (validFiles.length > 0) {
        setPendingFiles(prev => [...prev, ...validFiles]);
      }
    } else {
      // Modo edição: upload direto via hook
      const uploadPromises = Array.from(files).map(file => {
        const validation = validateFile(file);
        if (!validation.valid) {
          toast.error(`${file.name}: ${validation.error}`);
          return Promise.resolve();
        }
        return uploadFile(file).catch(error => {
          console.error(`Erro ao enviar ${file.name}:`, error);
        });
      });
      await Promise.allSettled(uploadPromises);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isCreateMode, uploadFile]);

  // Remove arquivo pendente da lista (modo criação)
  const handleRemovePending = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Deletar anexo existente (modo edição)
  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    setDeletingId(attachmentId);
    try { await deleteAttachment(attachmentId); }
    finally { setDeletingId(null); }
  }, [deleteAttachment]);

  // Clique em anexo - abre preview interno para todos os tipos
  const handleAttachmentClick = useCallback((attachment: TaskAttachment) => {
    const allItems: MediaItem[] = (attachments || []).map(a => ({
      url: a.public_url || a.thumbnail_url || '',
      type: resolvePreviewMediaType(a.file_type, (a as any).mime_type) ?? 'document',
      title: a.file_name,
    }));
    const idx = (attachments || []).findIndex(a => a.id === attachment.id);
    setLightboxMediaItems(allItems);
    setLightboxIndex(idx >= 0 ? idx : 0);
    setLightboxOpen(true);
  }, [attachments]);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Editor de texto rico para descrição */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Descrição</label>
        <RichTextEditor
          content={descriptionHtml}
          onChange={onDescriptionChange}
          readOnly={readOnly}
          placeholder="Adicione uma descrição detalhada da tarefa..."
          minHeight="100px"
          maxHeight="250px"
          showToolbar={!readOnly}
        />
      </div>

      {/* Área de anexos — sempre visível (criação e edição) */}
      {!readOnly && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Anexos</span>
            {totalCount > 0 && (
              <span className="text-xs text-muted-foreground">({totalCount})</span>
            )}
          </div>

          {/* Área de upload compartilhada */}
          <AttachmentUploadArea
            onUploadClick={() => fileInputRef.current?.click()}
            isUploading={isUploading}
            maxSizeLabel="Máx: 50MB por arquivo"
          />

          {/* Miniaturas de arquivos pendentes (modo criação - staged) */}
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {pendingFiles.map((file, index) => (
                <PendingFileThumbnail
                  key={`pending-${index}-${file.name}`}
                  file={file}
                  onRemove={() => handleRemovePending(index)}
                />
              ))}
            </div>
          )}

          {/* Miniaturas de anexos existentes (modo edição) */}
          {!isCreateMode && (
            <>
              {isLoadingAttachments ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              ) : existingCount > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {attachments?.map(attachment => (
                    <AttachmentThumbnail
                      key={attachment.id}
                      attachment={attachment}
                      readOnly={readOnly}
                      onDelete={() => handleDeleteAttachment(attachment.id)}
                      onClick={() => handleAttachmentClick(attachment)}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      {/* Input oculto para seleção de arquivos */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Lightbox para imagens */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />
    </div>
  );
}
